import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClickSound } from '../hooks/useClickSound';
import FullScreenModal from './FullScreenModal';
import {
  getSuperteamLeaderboard,
  getCountryLeaderboard,
  getGlobalLeaderboard,
} from '../utils/api';
import type {
  SuperteamLeaderboardEntry,
  CountryLeaderboardEntry,
  GlobalLeaderboardEntry,
} from '../types';

type TabType = 'superteams' | 'countries' | 'global';

interface LeaderboardProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Leaderboard({ isOpen, onClose }: LeaderboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('superteams');
  const [isMobile, setIsMobile] = useState(false);
  const { withClickSound } = useClickSound();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch leaderboard data
  const { data: superteamData, isLoading: superteamLoading } = useQuery({
    queryKey: ['leaderboard', 'superteams'],
    queryFn: getSuperteamLeaderboard,
    enabled: isOpen && activeTab === 'superteams',
    staleTime: 60000,
  });

  const { data: countryData, isLoading: countryLoading } = useQuery({
    queryKey: ['leaderboard', 'countries'],
    queryFn: getCountryLeaderboard,
    enabled: isOpen && activeTab === 'countries',
    staleTime: 60000,
  });

  const { data: globalData, isLoading: globalLoading } = useQuery({
    queryKey: ['leaderboard', 'global'],
    queryFn: getGlobalLeaderboard,
    enabled: isOpen && activeTab === 'global',
    staleTime: 60000,
  });

  const superteams = superteamData?.data?.leaderboard || [];
  const countries = countryData?.data?.leaderboard || [];
  const global = globalData?.data?.leaderboard || [];

  const isLoading = 
    (activeTab === 'superteams' && superteamLoading) ||
    (activeTab === 'countries' && countryLoading) ||
    (activeTab === 'global' && globalLoading);

  if (!isOpen) return null;

  // Style definitions (used by both mobile and desktop)
  const tabsStyle: CSSProperties = {
    display: 'flex',
    gap: '4px',
    padding: isMobile ? '12px 0' : '12px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  };

  const tabStyle = (isActive: boolean): CSSProperties => ({
    padding: isMobile ? '8px 12px' : '10px 20px',
    borderRadius: '10px',
    border: 'none',
    background: isActive ? 'linear-gradient(135deg, rgba(153, 69, 255, 0.3), rgba(20, 241, 149, 0.3))' : 'transparent',
    color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    flex: 1,
    textAlign: 'center',
  });

  const contentStyle: CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: isMobile ? '16px 0' : '16px 24px',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  };

  const rowStyle = (rank: number): CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: rank <= 3 
      ? `linear-gradient(135deg, ${rank === 1 ? 'rgba(255, 215, 0, 0.15)' : rank === 2 ? 'rgba(192, 192, 192, 0.15)' : 'rgba(205, 127, 50, 0.15)'}, transparent)`
      : 'rgba(255, 255, 255, 0.03)',
    borderRadius: '12px',
    marginBottom: '8px',
    border: rank <= 3 
      ? `1px solid ${rank === 1 ? 'rgba(255, 215, 0, 0.3)' : rank === 2 ? 'rgba(192, 192, 192, 0.3)' : 'rgba(205, 127, 50, 0.3)'}`
      : '1px solid rgba(255, 255, 255, 0.05)',
  });

  const rankStyle = (rank: number): CSSProperties => ({
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '14px',
    background: rank === 1 
      ? 'linear-gradient(135deg, #FFD700, #FFA500)' 
      : rank === 2 
        ? 'linear-gradient(135deg, #C0C0C0, #A0A0A0)'
        : rank === 3
          ? 'linear-gradient(135deg, #CD7F32, #8B4513)'
          : 'rgba(255, 255, 255, 0.1)',
    color: rank <= 3 ? '#000' : 'rgba(255, 255, 255, 0.7)',
  });

  const iconStyle: CSSProperties = {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    objectFit: 'cover' as const,
    border: '1px solid rgba(255, 255, 255, 0.1)',
  };

  const placeholderIconStyle: CSSProperties = {
    ...iconStyle,
    background: 'linear-gradient(135deg, #9945FF, #14F195)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
  };

  const nameStyle: CSSProperties = {
    flex: 1,
    fontWeight: 600,
    color: 'white',
    fontSize: '14px',
  };

  const countStyle: CSSProperties = {
    fontWeight: 700,
    color: '#14F195',
    fontSize: '16px',
    textAlign: 'right' as const,
  };

  const labelStyle: CSSProperties = {
    fontSize: '10px',
    color: 'rgba(255, 255, 255, 0.5)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  };

  const renderSuperteamRow = (entry: SuperteamLeaderboardEntry) => (
    <div key={entry.id} style={rowStyle(entry.rank)}>
      <div style={rankStyle(entry.rank)}>{entry.rank}</div>
      {entry.iconUrl ? (
        <img src={entry.iconUrl} alt={entry.name} style={iconStyle} />
      ) : (
        <div style={placeholderIconStyle}>
          {entry.name.charAt(0).toUpperCase()}
        </div>
      )}
      <div style={{ flex: 1 }}>
        <div style={nameStyle}>{entry.name}</div>
        <div style={labelStyle}>{entry.country}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={countStyle}>{entry.connectedMembers}</div>
        <div style={labelStyle}>members</div>
      </div>
    </div>
  );

  const renderCountryRow = (entry: CountryLeaderboardEntry) => (
    <div key={entry.code} style={rowStyle(entry.rank)}>
      <div style={rankStyle(entry.rank)}>{entry.rank}</div>
      <div style={placeholderIconStyle}>
        {entry.flagEmoji}
      </div>
      <div style={{ flex: 1 }}>
        <div style={nameStyle}>{entry.name}</div>
        <div style={labelStyle}>No Superteam</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={countStyle}>{entry.memberCount}</div>
        <div style={labelStyle}>members</div>
      </div>
    </div>
  );

  const renderGlobalRow = (entry: GlobalLeaderboardEntry) => (
    <div key={`${entry.type}-${entry.id}`} style={rowStyle(entry.rank)}>
      <div style={rankStyle(entry.rank)}>{entry.rank}</div>
      {entry.type === 'superteam' && entry.icon ? (
        <img src={entry.icon} alt={entry.name} style={iconStyle} />
      ) : (
        <div style={placeholderIconStyle}>
          {entry.type === 'country' ? entry.icon : entry.name.charAt(0).toUpperCase()}
        </div>
      )}
      <div style={{ flex: 1 }}>
        <div style={nameStyle}>{entry.name}</div>
        <div style={labelStyle}>{entry.type === 'superteam' ? 'Superteam' : 'Country'}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={countStyle}>{entry.memberCount}</div>
        <div style={labelStyle}>members</div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
          Loading leaderboard...
        </div>
      );
    }

    if (activeTab === 'superteams') {
      if (superteams.length === 0) {
        return (
          <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
            No superteams found
          </div>
        );
      }
      return superteams.map(renderSuperteamRow);
    }

    if (activeTab === 'countries') {
      if (countries.length === 0) {
        return (
          <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
            No countries without superteams found
          </div>
        );
      }
      return countries.map(renderCountryRow);
    }

    if (activeTab === 'global') {
      if (global.length === 0) {
        return (
          <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
            No data found
          </div>
        );
      }
      return global.map(renderGlobalRow);
    }

    return null;
  };

  // Mobile: Use FullScreenModal
  if (isMobile) {
    return (
      <FullScreenModal
        isOpen={isOpen}
        onClose={onClose}
        title="🏆 Leaderboard"
      >
        <div style={{ padding: '0 16px 16px' }}>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '-8px', marginBottom: '16px' }}>
            Ranked by connected members
          </p>
          
          {/* Tabs */}
          <div style={tabsStyle}>
            <button
              onClick={withClickSound(() => setActiveTab('superteams'))}
              style={tabStyle(activeTab === 'superteams')}
            >
              Superteams
            </button>
            <button
              onClick={withClickSound(() => setActiveTab('countries'))}
              style={tabStyle(activeTab === 'countries')}
            >
              Countries
            </button>
            <button
              onClick={withClickSound(() => setActiveTab('global'))}
              style={tabStyle(activeTab === 'global')}
            >
              Global
            </button>
          </div>

          {/* Content */}
          <div style={{ ...contentStyle, padding: '16px 0' }} className="custom-scrollbar">
            {renderContent()}
          </div>
        </div>
      </FullScreenModal>
    );
  }

  // Desktop: Use centered modal
  const overlayStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(8px)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  };

  const containerStyle: CSSProperties = {
    background: 'linear-gradient(135deg, rgba(15, 15, 35, 0.98) 0%, rgba(25, 25, 55, 0.98) 100%)',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '85vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  };

  const headerStyle: CSSProperties = {
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const closeButtonStyle: CSSProperties = {
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    borderRadius: '10px',
    padding: '10px',
    cursor: 'pointer',
    color: 'rgba(255, 255, 255, 0.7)',
    transition: 'all 0.2s ease',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={containerStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'white', margin: 0 }}>
              🏆 Leaderboard
            </h2>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
              Ranked by connected members
            </p>
          </div>
          <button 
            onClick={withClickSound(onClose)} 
            style={closeButtonStyle}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div style={tabsStyle}>
          <button
            onClick={withClickSound(() => setActiveTab('superteams'))}
            style={tabStyle(activeTab === 'superteams')}
          >
            Superteams
          </button>
          <button
            onClick={withClickSound(() => setActiveTab('countries'))}
            style={tabStyle(activeTab === 'countries')}
          >
            Countries
          </button>
          <button
            onClick={withClickSound(() => setActiveTab('global'))}
            style={tabStyle(activeTab === 'global')}
          >
            Global
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle} className="custom-scrollbar">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
